import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Chip,
    CircularProgress,
    Pagination,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import DownloadIcon from '@mui/icons-material/Download';
import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import { financeAPI } from '../../services/apiService';
import './Billings.css';

function BillingMobileCard({ bill, onDownload }) {
    const { t, i18n } = useTranslation();
    return (
        <Paper className="mobile_card">
            <div className="mobile_row">
                <span className="mobile_label">{t('invoice_number')}</span>
                <span className="mobile_value">{bill.uid}</span>
            </div>

            <div className="mobile_row">
                <span className="mobile_label">{t('amount')}</span>
                <span className="mobile_value">
                    {parseInt(bill.amount, 10).toLocaleString(
                        i18n.language === 'fa' ? 'fa-IR' : 'en-US'
                    )}
                    {t('toman')}
                </span>
            </div>

            <div className="mobile_row">
                <span className="mobile_label">{t('start_date')}</span>
                <span className="mobile_value">
                    {new Date(bill.created_date).toLocaleDateString(
                        i18n.language === 'fa' ? 'fa-IR' : 'en-US'
                    )}
                </span>
            </div>

            <div className="mobile_row">
                <span className="mobile_label">{t('end_date')}</span>
                <span className="mobile_value">
                    {new Date(bill.due_date).toLocaleDateString(
                        i18n.language === 'fa' ? 'fa-IR' : 'en-US'
                    )}
                </span>
            </div>

            <div className="mobile_row">
                <span className="mobile_label">{t('service-type')}</span>
                <span>{bill.service_type === 'Services' ? t('Service') : t('Product')}</span>
            </div>

            <div className="mobile_row">
                <span className="mobile_label">{t('status')}</span>
                <Chip
                    label={bill.is_paid ? t('paid') : t('unpaid')}
                    className={bill.is_paid ? 'paid_chip' : 'unpaid_chip'}
                />
            </div>

            <div className="mobile_actions">
                {!bill.is_paid && (
                    <IconButton size="small" color="primary">
                        <PaymentIcon />
                    </IconButton>
                )}

                <IconButton size="small" color="primary" onClick={() => onDownload(bill.pdf_bill)}>
                    <DownloadIcon />
                </IconButton>
            </div>
        </Paper>
    );
}

function Billings() {
    const { t, i18n } = useTranslation();
    const [bills, setBills] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);

    const itemsPerPage = 10;

    useEffect(() => {
        financeAPI
            .getMonthlyBills({ fallback: [] })
            .then((data) => {
                const list = Array.isArray(data) ? data : data.results || [];
                setBills(list);
            })
            .catch((err) => {
                console.error(err);
                setBills([]);
            })
            .finally(() => setLoading(false));
    }, []);

    const filteredBills = bills
        .filter((bill) => String(bill.uid).toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) =>
            String(b.uid).localeCompare(String(a.uid), undefined, {
                numeric: true,
                sensitivity: 'base',
            })
        );
    const paginatedBills = filteredBills.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    const totalPages = Math.ceil(filteredBills.length / itemsPerPage);

    const handleDownload = async (url) => {
        const blob = await financeAPI.downloadBill(url);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = url.split('/').pop();
        link.click();
        URL.revokeObjectURL(link.href);
    };

    return (
        <div className="billings_page">
            <Sidebar />

            <div className="billings_main">
                <Navbar />

                <div className="billings_content">
                    <div className="search_bar">
                        <input
                            type="text"
                            placeholder={t('search_invoice')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="user_search_input"
                        />
                    </div>

                    {loading ? (
                        <div className="loading_wrapper">
                            <CircularProgress />
                        </div>
                    ) : (
                        <>
                            <div className="desktop_only">
                                <TableContainer component={Paper} className="billings_table">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align="center">
                                                    {t('invoice_number')}
                                                </TableCell>
                                                <TableCell align="center">{t('amount')}</TableCell>
                                                <TableCell align="center">
                                                    {t('start_date')}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {t('end_date')}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {t('service-type')}
                                                </TableCell>
                                                <TableCell align="center">{t('status')}</TableCell>
                                                <TableCell align="center">
                                                    {t('download')}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedBills.map((bill) => (
                                                <TableRow key={bill.id}>
                                                    <TableCell align="center">{bill.uid}</TableCell>
                                                    <TableCell align="center">
                                                        {parseInt(bill.amount, 10).toLocaleString(
                                                            i18n.language === 'fa'
                                                                ? 'fa-IR'
                                                                : 'en-US'
                                                        )}
                                                        {t('toman')}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {new Date(
                                                            bill.created_date
                                                        ).toLocaleDateString(
                                                            i18n.language === 'fa'
                                                                ? 'fa-IR'
                                                                : 'en-US'
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {new Date(bill.due_date).toLocaleDateString(
                                                            i18n.language === 'fa'
                                                                ? 'fa-IR'
                                                                : 'en-US'
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {bill.service_type === 'Services'
                                                            ? t('Service')
                                                            : t('Product')}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={
                                                                bill.is_paid
                                                                    ? t('paid')
                                                                    : t('unpaid')
                                                            }
                                                            className={
                                                                bill.is_paid
                                                                    ? 'paid_chip'
                                                                    : 'unpaid_chip'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            className="downloadBtn"
                                                            onClick={() =>
                                                                handleDownload(bill.pdf_bill)
                                                            }
                                                        >
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>

                            <div className="mobile_only">
                                {paginatedBills.map((bill) => (
                                    <BillingMobileCard
                                        key={bill.id}
                                        bill={bill}
                                        onDownload={handleDownload}
                                    />
                                ))}
                            </div>
                        </>
                    )}

                    {totalPages > 1 && (
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            className="pagination"
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default Billings;
